/**
 * ServiceDesignerList — "Diseñador de servicios" catalog.
 * Lists CMDB services (real services from the existing module) and lets the
 * user open each one's graph in the designer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowTopRightOnSquareIcon, BeakerIcon, Squares2X2Icon, LinkIcon, PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ServiceDesignerModelBuilder from './ServiceDesignerModelBuilder';
import ServiceDesignerRelationTypes from './ServiceDesignerRelationTypes';
import ServiceDesignerNewServiceModal from './ServiceDesignerNewServiceModal';
import ServiceWizardRunner from './ServiceWizardRunner';
import ServiceWizardEditor from './ServiceWizardEditor';
import './ServiceDesigner.css';

const STATUS_COLORS = {
  running:     { bg: 'rgba(16,185,129,.16)',  color: '#6ee7b7', label: 'Operativo' },
  stopped:     { bg: 'rgba(239,68,68,.16)',   color: '#fca5a5', label: 'Parado' },
  maintenance: { bg: 'rgba(245,158,11,.16)',  color: '#fcd34d', label: 'Mantenimiento' },
  degraded:    { bg: 'rgba(249,115,22,.16)',  color: '#fdba74', label: 'Degradado' },
  decommissioned: { bg: 'rgba(148,163,184,.16)', color: '#cbd5e1', label: 'Retirado' },
  planned:     { bg: 'rgba(99,102,241,.16)',  color: '#a5b4fc', label: 'Planificado' },
};

export default function ServiceDesignerList({ companyId, onOpenService }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModels, setShowModels] = useState(false);
  const [showRelationTypes, setShowRelationTypes] = useState(false);
  const [showNewService, setShowNewService] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showWizardEditor, setShowWizardEditor] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.sdListServices(companyId);
      setServices(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="sd-list-page">
      <div className="sd-list-header">
        <div>
          <h2>🧩 Diseñador de servicios</h2>
          <p className="sd-list-subtitle">
            Une elementos existentes de la CMDB para diseñar cómo está montado un servicio completo.
          </p>
        </div>
        <div className="sd-list-actions">
          <button className="sd-refresh-btn sd-new-btn" onClick={() => setShowNewService(true)} title="Crear un servicio nuevo y abrirlo en el diseñador">
            <PlusIcon width={16} height={16} /> Nuevo servicio
          </button>
          <button className="sd-refresh-btn sd-wizard-btn" onClick={() => setShowWizard(true)} title="Crear un servicio con un asistente guiado">
            <SparklesIcon width={16} height={16} /> Asistentes
          </button>
          <button className="sd-refresh-btn" onClick={() => setShowModels(true)} title="Gestionar plantillas/modelos">
            <Squares2X2Icon width={16} height={16} /> Plantillas
          </button>
          <button className="sd-refresh-btn" onClick={() => setShowRelationTypes(true)} title="Gestionar tipos de relación">
            <LinkIcon width={16} height={16} /> Tipos de relación
          </button>
          <button className="sd-refresh-btn" onClick={() => setShowWizardEditor(true)} title="Crear y editar asistentes">
            <Squares2X2Icon width={16} height={16} /> Editar asistentes
          </button>
          <button className="sd-refresh-btn" onClick={load} disabled={loading}>
            {loading ? 'Cargando…' : 'Refrescar'}
          </button>
        </div>
      </div>

      {error && <div className="sd-error-banner">{error}</div>}

      <div className="sd-list-grid">
        {services.map((svc) => {
          const st = STATUS_COLORS[svc.status] || STATUS_COLORS.running;
          return (
            <div key={svc.id} className="sd-card">
              <div className="sd-card-top">
                <div className="sd-card-title">
                  <BeakerIcon width={18} height={18} />
                  <span>{svc.display_name || svc.name}</span>
                </div>
                <span className="sd-status-pill" style={{ background: st.bg, color: st.color }}>
                  {st.label}
                </span>
              </div>
              <div className="sd-card-meta">
                <span>{svc.service_type}</span>
                <span>· {svc.member_count ?? 0} elementos en el grafo</span>
              </div>
              <button
                className="sd-open-btn"
                onClick={() => onOpenService && onOpenService(svc.id, svc)}
              >
                <ArrowTopRightOnSquareIcon width={16} height={16} />
                Abrir diseñador
              </button>
            </div>
          );
        })}
        {!loading && services.length === 0 && (
          <div className="sd-empty">
            No hay servicios todavía. Crea un servicio en el módulo Servicios y vuelve aquí para
            diseñar su grafo de dependencias.
          </div>
        )}
      </div>

      {showModels && (
        <ServiceDesignerModelBuilder companyId={companyId} onClose={() => setShowModels(false)} onSaved={() => {}} />
      )}
      {showRelationTypes && (
        <ServiceDesignerRelationTypes companyId={companyId} onClose={() => setShowRelationTypes(false)} />
      )}
      {showNewService && (
        <ServiceDesignerNewServiceModal
          companyId={companyId}
          onClose={() => setShowNewService(false)}
          onCreated={(svc) => onOpenService && onOpenService(svc.id, svc)}
        />
      )}
      {showWizard && (
        <ServiceWizardRunner
          companyId={companyId}
          onClose={() => setShowWizard(false)}
          onCreated={(sid, name) => onOpenService && onOpenService(sid, { id: sid, name })}
        />
      )}
      {showWizardEditor && (
        <ServiceWizardEditor companyId={companyId} onClose={() => setShowWizardEditor(false)} />
      )}
    </div>
  );
}
